import { useState, useCallback, useMemo } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Card } from '../components/Card'
import { useCurrency } from '../lib/currency-context'
import { fmtMoney, FMT_INT } from '../lib/money'
import { CopyCell } from '../components/CopyCell'
import { useSortable } from '../hooks/useSortable'
import { SortableHeader } from '../components/SortableHeader'
import { MysysReport } from './MysysReport'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface OrphanRow {
  id: number
  tracking_number: string
  master_tracking_number: string | null
  carrier_service: string | null
  carrier_account: string | null
  customer_name: string | null
  customer_display_name: string | null
  create_date: string | null
  days_since_shipped: number | null
  currency_code: string
  label_cost_minor: number
  label_cost_usd_minor: number
  profit_usd_minor: number
  total_cost_usd_minor: number
  sender_country_code: string | null
  recipient_country_code: string | null
  package_weight_lb: number | null
}

interface OrphanList  { rows: OrphanRow[]; total: number; limit: number; offset: number }
interface CarrierBreakdown { carrier: string; count: number; pct: number }
interface OrphanSummary {
  total_count: number
  total_label_cost_usd_cents: number
  oldest_days: number | null
  newest_days: number | null
  pct_of_total: number
  by_carrier: CarrierBreakdown[]
}

interface GhostRow {
  carrier: string
  tracking_number: string
  invoice_date: string | null
  charge_amount: number | null
  charge_currency: string | null
  service_type: string | null
  invoice_id: string | null
  days_on_invoice: number | null
  is_mysys_candidate: boolean
}

interface GhostList { rows: GhostRow[]; total: number; limit: number; offset: number }
interface GhostCarrierBreakdown { carrier: string; count: number; total_charge: number; pct: number; is_mysys_candidate: boolean; mysys_count: number }
interface GhostServiceBreakdown { carrier: string; service_type: string; count: number; total_charge: number }
interface GhostSummary {
  total_count: number; total_charge: number; by_carrier: GhostCarrierBreakdown[]
  by_service: GhostServiceBreakdown[]
  mysys_count: number; mysys_charge: number; non_mysys_count: number
  non_mysys_charge: number; oldest_days: number | null
}

// ---------------------------------------------------------------------------
// Age-band helpers
// ---------------------------------------------------------------------------
function ageRowClass(days: number | null): string {
  if (days === null) return ''
  if (days > 45)  return 'bg-red-50'
  if (days >= 14) return 'bg-amber-50'
  return ''
}
function ageBadgeClass(days: number | null): string {
  if (days === null) return 'text-ink-500'
  if (days > 45)  return 'num font-semibold text-red-600'
  if (days >= 14) return 'num font-semibold text-amber-600'
  return 'num text-ink-700'
}
function ghostRowClass(row: GhostRow): string {
  return row.is_mysys_candidate ? 'bg-ink-50' : ageRowClass(row.days_on_invoice)
}

// ---------------------------------------------------------------------------
// Sortable column header
// ---------------------------------------------------------------------------
type SortDir = 'asc' | 'desc'
interface SortableThProps {
  label: string
  colKey: string
  sort: string
  onSort: (key: string) => void
  align?: 'left' | 'right'
}
function SortableTh({ label, colKey, sort, onSort, align = 'left' }: SortableThProps) {
  const active = sort.startsWith(colKey)
  const dir    = sort.endsWith('_asc') ? 'asc' : 'desc'
  return (
    <th
      className={`px-4 py-3 font-semibold text-ink-600 cursor-pointer select-none hover:text-ink-900 ${align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => onSort(colKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={`text-[10px] ${active ? 'text-royal-600' : 'text-ink-300'}`}>
          {active ? (dir === 'desc' ? '▼' : '▲') : '⇅'}
        </span>
      </span>
    </th>
  )
}

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200]

function Pagination({ total, limit, offset, onPage, onLimitChange }: {
  total: number; limit: number; offset: number
  onPage: (o: number) => void; onLimitChange: (l: number) => void
}) {
  const { t } = useTranslation()
  const totalPages  = Math.max(1, Math.ceil(total / limit))
  const currentPage = Math.floor(offset / limit) + 1

  const pages = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
    const result: number[] = [1]
    if (currentPage > 3) result.push(-1)
    for (let p = Math.max(2, currentPage - 2); p <= Math.min(totalPages - 1, currentPage + 2); p++) result.push(p)
    if (currentPage < totalPages - 2) result.push(-1)
    result.push(totalPages)
    return result
  }, [totalPages, currentPage])

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-ink-200 bg-ink-50 flex-wrap gap-2">
      <div className="flex items-center gap-2 text-[12px] text-ink-500">
        <span>{FMT_INT.format(total)} {t('orphans.total_rows')}</span>
        <span className="text-ink-300">·</span>
        <select value={limit} onChange={e => onLimitChange(Number(e.target.value))}
          className="border border-ink-200 rounded px-2 py-1 text-[12px] bg-white text-ink-700">
          {PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s} {t('orphans.per_page')}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => onPage(0)} disabled={currentPage === 1}
          className="px-2 py-1 text-[12px] font-semibold border border-ink-200 rounded disabled:opacity-40 hover:bg-ink-100" title="First">«</button>
        <button onClick={() => onPage(Math.max(0, offset - limit))} disabled={currentPage === 1}
          className="px-2 py-1 text-[12px] font-semibold border border-ink-200 rounded disabled:opacity-40 hover:bg-ink-100" title="Previous">‹</button>
        {pages.map((p, i) => p === -1 ? (
          <span key={`e-${i}`} className="px-2 text-[12px] text-ink-400 select-none">…</span>
        ) : (
          <button key={p} onClick={() => onPage((p - 1) * limit)}
            className={`min-w-[30px] px-2 py-1 text-[12px] font-semibold border rounded ${p === currentPage ? 'bg-royal-600 text-white border-royal-600' : 'border-ink-200 hover:bg-ink-100 text-ink-700'}`}>
            {p}
          </button>
        ))}
        <button onClick={() => onPage(offset + limit)} disabled={currentPage >= totalPages}
          className="px-2 py-1 text-[12px] font-semibold border border-ink-200 rounded disabled:opacity-40 hover:bg-ink-100" title="Next">›</button>
        <button onClick={() => onPage((totalPages - 1) * limit)} disabled={currentPage >= totalPages}
          className="px-2 py-1 text-[12px] font-semibold border border-ink-200 rounded disabled:opacity-40 hover:bg-ink-100" title="Last">»</button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Age preset buttons
// ---------------------------------------------------------------------------
const AGE_PRESETS = [
  { label: '>30d',  value: 30 },
  { label: '>90d',  value: 90 },
  { label: '>180d', value: 180 },
]

function AgePresets({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[12px] text-ink-500">Age ≥</span>
      <input
        type="number"
        min={0}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="days"
        className="border border-ink-200 rounded-lg px-2 py-2 text-[13px] w-20 bg-white text-ink-700"
      />
      <span className="text-[12px] text-ink-400">d</span>
      {AGE_PRESETS.map(p => (
        <button
          key={p.value}
          onClick={() => onChange(value === String(p.value) ? '' : String(p.value))}
          className={`px-2 py-1.5 text-[11px] font-semibold rounded border ${
            value === String(p.value)
              ? 'bg-amber-500 text-white border-amber-500'
              : 'border-ink-200 text-ink-600 hover:bg-ink-50'
          }`}
        >
          {p.label}
        </button>
      ))}
      {value && (
        <button onClick={() => onChange('')} className="text-[12px] text-ink-400 hover:text-ink-700">✕</button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Select-all-matching banner
// ---------------------------------------------------------------------------
function SelectAllBanner({ pageCount, total, allMatchingSelected, onSelectMatching, onClearMatching }: {
  pageCount: number; total: number; allMatchingSelected: boolean
  onSelectMatching: () => void; onClearMatching: () => void
}) {
  if (pageCount === 0 || total <= pageCount) return null
  if (allMatchingSelected) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-royal-50 border-b border-royal-100 text-[12px] text-royal-800">
        <span>All <strong>{FMT_INT.format(total)}</strong> matching rows selected.</span>
        <button onClick={onClearMatching} className="underline hover:no-underline">Clear selection</button>
      </div>
    )
  }
  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-royal-50 border-b border-royal-100 text-[12px] text-royal-800">
      <span>All {pageCount} on this page selected.</span>
      <button onClick={onSelectMatching} className="underline font-semibold hover:no-underline">
        Select all {FMT_INT.format(total)} matching results
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------
export function OrphansView() {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const qc = useQueryClient()

  const [activeTab, setActiveTab] = useState<'unmatched' | 'ghost' | 'mysys'>('unmatched')

  // ---- Unmatched filter state ----
  const [carrierPrefix, setCarrierPrefix] = useState('')
  const [carrier, setCarrier]             = useState('')
  const [customerInput, setCustomerInput] = useState('')
  const [customer, setCustomer]           = useState('')
  const [dateFrom, setDateFrom]           = useState('')
  const [dateTo, setDateTo]               = useState('')
  const [minAgeDays, setMinAgeDays]       = useState('')
  const [sort, setSort]                   = useState('days_since_shipped_desc')
  const [offset, setOffset]               = useState(0)
  const [limit, setLimit]                 = useState(50)

  // ---- Unmatched selection state ----
  const [selectedIds, setSelectedIds]           = useState<Set<number>>(new Set())
  const [selectAllMatching, setSelectAllMatching] = useState(false)

  // ---- Ghost filter state ----
  const [ghostCarrier, setGhostCarrier]       = useState('')
  const [ghostService, setGhostService]       = useState('')
  const [excludeMysys, setExcludeMysys]       = useState(false)
  const [ghostDateFrom, setGhostDateFrom]     = useState('')
  const [ghostDateTo, setGhostDateTo]         = useState('')
  const [ghostMinAgeDays, setGhostMinAgeDays] = useState('')
  const [ghostSort, setGhostSort]             = useState('days_on_invoice_desc')
  const [ghostOffset, setGhostOffset]         = useState(0)
  const [ghostLimit, setGhostLimit]           = useState(50)

  // ---- Ghost selection state ----
  const [selectedGhostKeys, setSelectedGhostKeys]       = useState<Set<string>>(new Set())
  const [selectAllGhostMatching, setSelectAllGhostMatching] = useState(false)

  // Reset helpers — clear offset + selection when filters change
  const resetUnmatched = () => { setOffset(0); setSelectedIds(new Set()); setSelectAllMatching(false) }
  const resetGhost     = () => { setGhostOffset(0); setSelectedGhostKeys(new Set()); setSelectAllGhostMatching(false) }

  // Clear all filters for each tab
  const clearUnmatched = () => {
    setCarrierPrefix(''); setCarrier(''); setCustomerInput(''); setCustomer('')
    setDateFrom(''); setDateTo(''); setMinAgeDays('')
    setOffset(0); setSelectedIds(new Set()); setSelectAllMatching(false)
  }
  const clearGhost = () => {
    setGhostCarrier(''); setGhostService(''); setGhostDateFrom(''); setGhostDateTo('')
    setGhostMinAgeDays(''); setExcludeMysys(false)
    setGhostOffset(0); setSelectedGhostKeys(new Set()); setSelectAllGhostMatching(false)
  }

  const handleCustomerSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    setCustomer(customerInput)
    setOffset(0); setSelectedIds(new Set()); setSelectAllMatching(false)
  }, [customerInput])

  // Sort toggler — clicking same column flips direction
  const handleSort = (col: string) => {
    const current = sort
    const dir = current === `${col}_desc` ? 'asc' : 'desc'
    setSort(`${col}_${dir}`)
    resetUnmatched()
  }
  const handleGhostSort = (col: string) => {
    const dir = ghostSort === `${col}_desc` ? 'asc' : 'desc'
    setGhostSort(`${col}_${dir}`)
    resetGhost()
  }

  // ---- Ignore mutations ----
  const ignoreMutation = useMutation({
    mutationFn: async (payload:
      | { mode: 'selected'; entries: Array<{ tracking_number: string; carrier?: string; type: 'unmatched' | 'ghost' }> }
      | { mode: 'matching'; type: 'unmatched' | 'ghost'; filters: Record<string, string> }
    ) => {
      if (payload.mode === 'selected') {
        const r = await fetch('/api/orphans/ignore', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ entries: payload.entries }),
        })
        if (!r.ok) throw new Error('ignore failed')
        return r.json()
      } else {
        const endpoint = payload.type === 'ghost'
          ? '/api/orphans/ghost/ignore-matching'
          : '/api/orphans/ignore-matching'
        const r = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload.filters),
        })
        if (!r.ok) throw new Error('ignore-matching failed')
        return r.json()
      }
    },
    onSuccess: () => {
      setSelectedIds(new Set())
      setSelectAllMatching(false)
      setSelectedGhostKeys(new Set())
      setSelectAllGhostMatching(false)
      qc.invalidateQueries({ queryKey: ['orphans'] })
      qc.invalidateQueries({ queryKey: ['orphans-summary'] })
      qc.invalidateQueries({ queryKey: ['ghost'] })
      qc.invalidateQueries({ queryKey: ['ghost-summary'] })
    },
  })

  // -------------------------------------------------------------------------
  // Unmatched queries
  // -------------------------------------------------------------------------
  const listParams = new URLSearchParams()
  if (carrier)            listParams.set('carrier',        carrier)
  else if (carrierPrefix) listParams.set('carrier_prefix', carrierPrefix)
  if (customer)   listParams.set('customer',     customer)
  if (dateFrom)   listParams.set('date_from',    dateFrom)
  if (dateTo)     listParams.set('date_to',      dateTo)
  if (minAgeDays) listParams.set('min_age_days', minAgeDays)
  listParams.set('sort',   sort)
  listParams.set('limit',  String(limit))
  listParams.set('offset', String(offset))

  const { data: summary, dataUpdatedAt: summaryUpdated } = useQuery<OrphanSummary>({
    queryKey: ['orphans-summary'],
    queryFn:  () => fetch('/api/orphans/summary').then(r => r.json()),
    staleTime: 60_000,
    refetchInterval: 3 * 60 * 1000,
  })

  // Unique carrier names (prefix before " - ") with aggregate counts
  const carrierPrefixes = useMemo(() => {
    const map = new Map<string, number>()
    for (const c of (summary?.by_carrier ?? [])) {
      const dash = c.carrier.indexOf(' - ')
      const prefix = dash >= 0 ? c.carrier.slice(0, dash) : c.carrier
      map.set(prefix, (map.get(prefix) ?? 0) + c.count)
    }
    return [...map.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
  }, [summary?.by_carrier])

  // Services visible in the service dropdown (all, or filtered by selected carrier prefix)
  const filteredServices = useMemo(() => {
    if (!carrierPrefix) return summary?.by_carrier ?? []
    return (summary?.by_carrier ?? []).filter(c => {
      const dash = c.carrier.indexOf(' - ')
      const prefix = dash >= 0 ? c.carrier.slice(0, dash) : c.carrier
      return prefix === carrierPrefix
    })
  }, [summary?.by_carrier, carrierPrefix])

  const serviceLabel = (full: string) => {
    const dash = full.indexOf(' - ')
    return dash >= 0 ? full.slice(dash + 3) : full
  }

  const { data: list, dataUpdatedAt: listUpdated, isLoading } = useQuery<OrphanList>({
    queryKey: ['orphans', carrierPrefix, carrier, customer, dateFrom, dateTo, minAgeDays, sort, offset, limit],
    queryFn:  () => fetch(`/api/orphans?${listParams}`).then(r => r.json()),
    staleTime: 60_000,
    refetchInterval: 3 * 60 * 1000,
    placeholderData: (prev) => prev,
  })

  // -------------------------------------------------------------------------
  // Ghost queries
  // -------------------------------------------------------------------------
  const ghostParams = new URLSearchParams()
  if (ghostCarrier)   ghostParams.set('carrier',       ghostCarrier)
  if (ghostService)   ghostParams.set('service_type', ghostService)
  if (excludeMysys)   ghostParams.set('exclude_mysys','true')
  if (ghostDateFrom)  ghostParams.set('date_from',    ghostDateFrom)
  if (ghostDateTo)    ghostParams.set('date_to',      ghostDateTo)
  if (ghostMinAgeDays) ghostParams.set('min_age_days', ghostMinAgeDays)
  ghostParams.set('sort',   ghostSort)
  ghostParams.set('limit',  String(ghostLimit))
  ghostParams.set('offset', String(ghostOffset))

  const { data: ghostSummary, dataUpdatedAt: ghostSummaryUpdated } = useQuery<GhostSummary>({
    queryKey: ['ghost-summary'],
    queryFn:  () => fetch('/api/orphans/ghost/summary').then(r => r.json()),
    staleTime: 60_000,
    refetchInterval: 3 * 60 * 1000,
  })

  // Services visible in ghost service dropdown (filtered by selected carrier, or all)
  const filteredGhostServices = useMemo(() => {
    if (!ghostCarrier) return ghostSummary?.by_service ?? []
    return (ghostSummary?.by_service ?? []).filter(s => s.carrier === ghostCarrier)
  }, [ghostSummary?.by_service, ghostCarrier])

  const { data: ghostList, dataUpdatedAt: ghostListUpdated, isLoading: ghostLoading } = useQuery<GhostList>({
    queryKey: ['ghost', ghostCarrier, ghostService, excludeMysys, ghostDateFrom, ghostDateTo, ghostMinAgeDays, ghostSort, ghostOffset, ghostLimit],
    queryFn:  () => fetch(`/api/orphans/ghost?${ghostParams}`).then(r => r.json()),
    staleTime: 60_000,
    refetchInterval: 3 * 60 * 1000,
    placeholderData: (prev) => prev,
  })

  // -------------------------------------------------------------------------
  // Refresh
  // -------------------------------------------------------------------------
  const lastRefreshed = new Date(Math.max(summaryUpdated, listUpdated, ghostSummaryUpdated, ghostListUpdated))

  const handleRefresh = () => {
    qc.invalidateQueries({ queryKey: ['orphans'] })
    qc.invalidateQueries({ queryKey: ['orphans-summary'] })
    qc.invalidateQueries({ queryKey: ['ghost'] })
    qc.invalidateQueries({ queryKey: ['ghost-summary'] })
  }

  const maxBarCount      = summary?.by_carrier?.[0]?.count ?? 1
  const ghostMaxBarCount = ghostSummary?.by_carrier?.[0]?.count ?? 1
  const hasUspsGhost     = (ghostSummary?.by_carrier ?? []).some(c => c.carrier === 'USPS')

  // -------------------------------------------------------------------------
  // Unmatched selection
  // -------------------------------------------------------------------------
  const currentRows    = list?.rows ?? []
  const { sort: unmatchedSort, onSort: onUnmatchedSort, sorted: sortedUnmatched } = useSortable(currentRows, 'create_date', 'desc')
  const allOnPageIds   = currentRows.map(r => r.id)
  const allPageSelected = allOnPageIds.length > 0 && allOnPageIds.every(id => selectedIds.has(id))

  const toggleSelectAll = () => {
    if (selectAllMatching) { setSelectAllMatching(false); setSelectedIds(new Set()); return }
    if (allPageSelected) {
      setSelectedIds(prev => { const n = new Set(prev); allOnPageIds.forEach(id => n.delete(id)); return n })
    } else {
      setSelectedIds(prev => { const n = new Set(prev); allOnPageIds.forEach(id => n.add(id)); return n })
    }
    setSelectAllMatching(false)
  }

  const toggleRow = (id: number) => {
    setSelectAllMatching(false)
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  }

  const handleIgnoreUnmatched = () => {
    if (selectAllMatching) {
      ignoreMutation.mutate({
        mode: 'matching', type: 'unmatched',
        filters: {
          ...(carrier    ? { carrier }    : {}),
          ...(customer   ? { customer }   : {}),
          ...(dateFrom   ? { date_from: dateFrom } : {}),
          ...(dateTo     ? { date_to: dateTo }     : {}),
          ...(minAgeDays ? { min_age_days: minAgeDays } : {}),
        },
      })
    } else {
      const entries = currentRows
        .filter(r => selectedIds.has(r.id))
        .map(r => ({ tracking_number: r.tracking_number, type: 'unmatched' as const }))
      ignoreMutation.mutate({ mode: 'selected', entries })
    }
  }

  // -------------------------------------------------------------------------
  // Ghost selection
  // -------------------------------------------------------------------------
  const currentGhostRows   = ghostList?.rows ?? []
  const { sort: ghostSortState, onSort: onGhostSortClient, sorted: sortedGhost } = useSortable(currentGhostRows, 'invoice_date', 'desc')
  const ghostKey           = (row: GhostRow) => `${row.carrier}:${row.tracking_number}`
  const allGhostOnPageKeys = currentGhostRows.map(ghostKey)
  const allGhostPageSelected = allGhostOnPageKeys.length > 0 && allGhostOnPageKeys.every(k => selectedGhostKeys.has(k))

  const toggleSelectAllGhost = () => {
    if (selectAllGhostMatching) { setSelectAllGhostMatching(false); setSelectedGhostKeys(new Set()); return }
    if (allGhostPageSelected) {
      setSelectedGhostKeys(prev => { const n = new Set(prev); allGhostOnPageKeys.forEach(k => n.delete(k)); return n })
    } else {
      setSelectedGhostKeys(prev => { const n = new Set(prev); allGhostOnPageKeys.forEach(k => n.add(k)); return n })
    }
    setSelectAllGhostMatching(false)
  }

  const toggleGhostRow = (row: GhostRow) => {
    setSelectAllGhostMatching(false)
    const k = ghostKey(row)
    setSelectedGhostKeys(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n })
  }

  const handleIgnoreGhost = () => {
    if (selectAllGhostMatching) {
      ignoreMutation.mutate({
        mode: 'matching', type: 'ghost',
        filters: {
          ...(ghostCarrier   ? { carrier: ghostCarrier }        : {}),
          ...(ghostDateFrom  ? { date_from: ghostDateFrom }      : {}),
          ...(ghostDateTo    ? { date_to: ghostDateTo }          : {}),
          ...(ghostMinAgeDays ? { min_age_days: ghostMinAgeDays } : {}),
          ...(excludeMysys   ? { exclude_mysys: 'true' }         : {}),
        },
      })
    } else {
      const entries = currentGhostRows
        .filter(r => selectedGhostKeys.has(ghostKey(r)))
        .map(r => ({ tracking_number: r.tracking_number, carrier: r.carrier, type: 'ghost' as const }))
      ignoreMutation.mutate({ mode: 'selected', entries })
    }
  }

  // ---- Selection counts for ignore button label ----
  const unmatchedSelCount = selectAllMatching ? (list?.total ?? 0) : selectedIds.size
  const ghostSelCount     = selectAllGhostMatching ? (ghostList?.total ?? 0) : selectedGhostKeys.size

  const dateInputClass = "border border-ink-200 rounded-lg px-3 py-2 text-[13px] bg-white text-ink-700 w-36"

  // =========================================================================
  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="text-[12px] font-semibold text-royal-600 uppercase tracking-wider">Reconciliation</div>
        <h1 className="text-[32px] font-bold tracking-tight mt-2">{t('orphans.title')}</h1>
        <p className="text-ink-500 text-[14px] mt-2">
          {activeTab === 'unmatched' ? t('orphans.unmatched_subtitle')
            : activeTab === 'ghost' ? t('orphans.ghost_subtitle') : t('orphans.mysys_subtitle')}
        </p>
      </div>

      <div className="flex items-center justify-end gap-4 mb-4">
        <span className="text-[12px] text-ink-500">
          {t('orphans.last_refreshed')}: {lastRefreshed.getTime() > 0 ? lastRefreshed.toLocaleTimeString() : '—'}
        </span>
        <button onClick={handleRefresh} className="text-[12px] font-semibold text-royal-600 hover:text-royal-700 underline-offset-2 hover:underline">
          {t('orphans.refresh')}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-ink-200">
        {(['unmatched', 'ghost', 'mysys'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-[13px] font-semibold border-b-2 -mb-px transition-colors ${
              activeTab === tab ? 'border-royal-600 text-royal-700' : 'border-transparent text-ink-500 hover:text-ink-700'
            }`}
          >
            {tab === 'unmatched' ? t('orphans.unmatched_tab') : tab === 'ghost' ? t('orphans.ghost_tab') : t('orphans.mysys_tab')}
            {tab === 'unmatched' && (summary?.total_count ?? 0) > 0 && (
              <span className="ml-2 px-1.5 py-0.5 rounded-full bg-ink-100 text-ink-600 text-[10px] font-bold">
                {FMT_INT.format(summary!.total_count)}
              </span>
            )}
            {tab === 'ghost' && (ghostSummary?.non_mysys_count ?? 0) > 0 && (
              <span className="ml-2 px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold">
                {FMT_INT.format(ghostSummary!.non_mysys_count)}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'mysys' && <MysysReport />}

      {/* ================================================================ */}
      {/* TAB 1 — Unmatched Shipments                                       */}
      {/* ================================================================ */}
      {activeTab === 'unmatched' && (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Card className="p-5">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500 mb-1">{t('orphans.total_orphans')}</div>
              <div className="num font-bold text-[28px]">{FMT_INT.format(summary?.total_count ?? 0)}</div>
            </Card>
            <Card className="p-5">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500 mb-1">{t('orphans.at_risk')}</div>
              <div className="num font-bold text-[28px] text-flame-600">
                {fmtMoney(summary?.total_label_cost_usd_cents ?? 0, currency, { compact: true })}
              </div>
            </Card>
            <Card className="p-5">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500 mb-1">{t('orphans.oldest')}</div>
              <div className={`font-bold text-[28px] num ${(summary?.oldest_days ?? 0) > 45 ? 'text-red-600' : (summary?.oldest_days ?? 0) >= 14 ? 'text-amber-600' : 'text-ink-900'}`}>
                {summary?.oldest_days ?? '—'}
              </div>
            </Card>
            <Card className="p-5">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500 mb-1">{t('orphans.pct_of_total')}</div>
              <div className="num font-bold text-[28px]">
                {summary?.pct_of_total != null ? `${summary.pct_of_total.toFixed(1)}%` : '—'}
              </div>
            </Card>
          </div>

          {/* Carrier breakdown + legend */}
          <div className="grid grid-cols-12 gap-4 mb-6">
            <Card className="col-span-7 p-5">
              <div className="text-[15px] font-semibold mb-3">{t('orphans.carrier_breakdown')}</div>
              {(summary?.by_carrier ?? []).length === 0 && <div className="text-[13px] text-ink-500">{t('orphans.no_orphans')}</div>}
              <div className="space-y-3">
                {(summary?.by_carrier ?? []).map(c => (
                  <div key={c.carrier}>
                    <div className="flex items-center justify-between text-[13px] mb-1">
                      <span className="font-medium">{c.carrier}</span>
                      <span className="num text-ink-500">{FMT_INT.format(c.count)} <span className="text-ink-400">({c.pct.toFixed(1)}%)</span></span>
                    </div>
                    <div className="h-2 rounded-full bg-ink-100 overflow-hidden">
                      <div className="h-full rounded-full bg-royal-500" style={{ width: `${Math.round((c.count / maxBarCount) * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
            <Card className="col-span-5 p-5">
              <div className="text-[15px] font-semibold mb-3">Age legend</div>
              <div className="space-y-3 text-[13px]">
                <div className="flex items-center gap-3"><div className="w-3 h-3 rounded-full bg-mint-500 shrink-0" /><span>{t('orphans.age_ok')}</span></div>
                <div className="flex items-center gap-3"><div className="w-3 h-3 rounded-full bg-amber-400 shrink-0" /><span>{t('orphans.age_warn')}</span></div>
                <div className="flex items-center gap-3"><div className="w-3 h-3 rounded-full bg-red-500 shrink-0" /><span>{t('orphans.age_danger')}</span></div>
              </div>
            </Card>
          </div>

          {/* Filters */}
          <Card className="p-4 mb-4">
            <div className="flex flex-wrap gap-3 items-center">
              {/* Carrier name */}
              <select value={carrierPrefix} onChange={e => { setCarrierPrefix(e.target.value); setCarrier(''); resetUnmatched() }}
                className="border border-ink-200 rounded-lg px-3 py-2 text-[13px] bg-white text-ink-700">
                <option value="">{t('orphans.filter_carrier')}</option>
                {carrierPrefixes.map(({ name, count }) => (
                  <option key={name} value={name}>{name} ({count})</option>
                ))}
              </select>

              {/* Service */}
              <select value={carrier} onChange={e => { setCarrier(e.target.value); resetUnmatched() }}
                className="border border-ink-200 rounded-lg px-3 py-2 text-[13px] bg-white text-ink-700">
                <option value="">All services</option>
                {filteredServices.map(c => (
                  <option key={c.carrier} value={c.carrier}>{serviceLabel(c.carrier)} ({c.count})</option>
                ))}
              </select>

              {/* Date range */}
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-ink-500">{t('orphans.filter_date_from')}</span>
                <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); resetUnmatched() }} className={dateInputClass} />
                <span className="text-[12px] text-ink-500">{t('orphans.filter_date_to')}</span>
                <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); resetUnmatched() }} className={dateInputClass} />
                {(dateFrom || dateTo) && <button onClick={() => { setDateFrom(''); setDateTo(''); resetUnmatched() }} className="text-[12px] text-ink-400 hover:text-ink-700">✕</button>}
              </div>

              {/* Age filter */}
              <AgePresets value={minAgeDays} onChange={v => { setMinAgeDays(v); resetUnmatched() }} />

              {/* Customer — exact, fires on Enter */}
              <form onSubmit={handleCustomerSubmit} className="flex items-center gap-2">
                <input type="text" value={customerInput} onChange={e => setCustomerInput(e.target.value)}
                  placeholder={t('orphans.filter_customer_placeholder')}
                  className="border border-ink-200 rounded-lg px-3 py-2 text-[13px] w-52 bg-white text-ink-700" />
                <button type="button" onClick={clearUnmatched} className="h-8 px-3 border border-ink-200 text-ink-500 rounded-md text-[12px] hover:bg-ink-50 transition-colors">Clear</button>
                <button type="submit" className="px-3 py-2 text-[13px] font-semibold bg-royal-600 text-white rounded-lg hover:bg-royal-700">Filter</button>
                {customer && <button type="button" onClick={() => { setCustomer(''); setCustomerInput(''); resetUnmatched() }} className="px-3 py-2 text-[13px] text-ink-500 hover:text-ink-700">Clear</button>}
              </form>

              {/* Ignore button */}
              {unmatchedSelCount > 0 && (
                <button onClick={handleIgnoreUnmatched} disabled={ignoreMutation.isPending}
                  className="ml-auto px-4 py-2 text-[13px] font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                  {ignoreMutation.isPending ? '…' : t('orphans.ignore_selected', { count: unmatchedSelCount })}
                </button>
              )}
            </div>
          </Card>

          {/* Table */}
          <Card className="overflow-hidden">
            {/* Select-all-matching banner */}
            {allPageSelected && !selectAllMatching && (
              <SelectAllBanner
                pageCount={currentRows.length}
                total={list?.total ?? 0}
                allMatchingSelected={false}
                onSelectMatching={() => setSelectAllMatching(true)}
                onClearMatching={() => { setSelectAllMatching(false); setSelectedIds(new Set()) }}
              />
            )}
            {selectAllMatching && (
              <SelectAllBanner
                pageCount={currentRows.length}
                total={list?.total ?? 0}
                allMatchingSelected={true}
                onSelectMatching={() => {}}
                onClearMatching={() => { setSelectAllMatching(false); setSelectedIds(new Set()) }}
              />
            )}

            {isLoading ? (
              <div className="p-8 text-center text-[13px] text-ink-500">Loading…</div>
            ) : currentRows.length === 0 ? (
              <div className="p-8 text-center text-[13px] text-ink-500">{t('orphans.no_orphans')}</div>
            ) : (
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-ink-50 border-b border-ink-200">
                    <th className="px-3 py-3 w-8">
                      <input type="checkbox" checked={allPageSelected || selectAllMatching} onChange={toggleSelectAll}
                        className="rounded border-ink-300 cursor-pointer" title={t('orphans.select_all')} />
                    </th>
                    <SortableHeader col="tracking_number"      label={t('orphans.tracking')}   sort={unmatchedSort} onSort={onUnmatchedSort} className="px-4 py-3" />
                    <SortableHeader col="carrier_service"      label={t('orphans.carrier')}    sort={unmatchedSort} onSort={onUnmatchedSort} className="px-4 py-3" />
                    <SortableHeader col="customer_name"        label={t('orphans.customer')}   sort={unmatchedSort} onSort={onUnmatchedSort} className="px-4 py-3" />
                    <SortableHeader col="create_date"          label={t('orphans.shipped')}    sort={unmatchedSort} onSort={onUnmatchedSort} className="px-4 py-3" />
                    <SortableHeader col="days_since_shipped"   label={t('orphans.age_days')}   sort={unmatchedSort} onSort={onUnmatchedSort} className="px-4 py-3" numeric />
                    <SortableHeader col="label_cost_usd_minor" label={t('orphans.label_cost')} sort={unmatchedSort} onSort={onUnmatchedSort} className="px-4 py-3" numeric />
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {sortedUnmatched.map(row => (
                    <tr key={row.id}
                      className={`${ageRowClass(row.days_since_shipped)} ${(selectedIds.has(row.id) || selectAllMatching) ? 'ring-1 ring-inset ring-royal-300' : ''}`}>
                      <td className="px-3 py-2.5">
                        <input type="checkbox" checked={selectedIds.has(row.id) || selectAllMatching}
                          onChange={() => toggleRow(row.id)} className="rounded border-ink-300 cursor-pointer" />
                      </td>
                      <td className="px-4 py-2.5"><CopyCell value={row.tracking_number} /></td>
                      <td className="px-4 py-2.5 text-ink-700">{row.carrier_service ?? '—'}</td>
                      <td className="px-4 py-2.5 text-ink-700">{row.customer_display_name ?? row.customer_name ?? '—'}</td>
                      <td className="px-4 py-2.5 text-ink-500">{row.create_date ? row.create_date.slice(0, 10) : '—'}</td>
                      <td className={`px-4 py-2.5 text-right ${ageBadgeClass(row.days_since_shipped)}`}>{row.days_since_shipped ?? '—'}</td>
                      <td className="px-4 py-2.5 text-right num text-ink-700">{fmtMoney(row.label_cost_usd_minor, currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {(list?.total ?? 0) > 0 && (
              <Pagination total={list?.total ?? 0} limit={limit} offset={offset}
                onPage={o => { setOffset(o); setSelectAllMatching(false) }}
                onLimitChange={l => { setLimit(l); setOffset(0); setSelectAllMatching(false) }} />
            )}
          </Card>
        </>
      )}

      {/* ================================================================ */}
      {/* TAB 2 — Ghost Charges                                             */}
      {/* ================================================================ */}
      {activeTab === 'ghost' && (
        <>
          {hasUspsGhost && (
            <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-6 text-[13px] text-blue-800">
              <span className="shrink-0 mt-0.5">ℹ️</span>
              <span>{t('orphans.mysys_info')}</span>
            </div>
          )}

          {/* KPI strip */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Card className="p-5">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500 mb-1">{t('orphans.total_ghost')}</div>
              <div className="num font-bold text-[28px]">{FMT_INT.format(ghostSummary?.total_count ?? 0)}</div>
              {(ghostSummary?.mysys_count ?? 0) > 0 && (
                <div className="text-[11px] text-ink-400 mt-1 num">{FMT_INT.format(ghostSummary!.mysys_count)} MYSYS (expected)</div>
              )}
            </Card>
            <Card className="p-5">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500 mb-1">{t('orphans.total_charge')}</div>
              <div className="num font-bold text-[28px]">
                ${(ghostSummary?.total_charge ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </Card>
            <Card className="p-5">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500 mb-1">{t('orphans.non_mysys')}</div>
              <div className={`num font-bold text-[28px] ${(ghostSummary?.non_mysys_count ?? 0) > 0 ? 'text-amber-600' : 'text-ink-900'}`}>
                {FMT_INT.format(ghostSummary?.non_mysys_count ?? 0)}
              </div>
              {(ghostSummary?.non_mysys_charge ?? 0) > 0 && (
                <div className="text-[11px] text-amber-600 mt-1 num font-semibold">
                  ${ghostSummary!.non_mysys_charge.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              )}
            </Card>
            <Card className="p-5">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500 mb-1">{t('orphans.oldest')}</div>
              <div className={`font-bold text-[28px] num ${(ghostSummary?.oldest_days ?? 0) > 45 ? 'text-red-600' : (ghostSummary?.oldest_days ?? 0) >= 14 ? 'text-amber-600' : 'text-ink-900'}`}>
                {ghostSummary?.oldest_days ?? '—'}
              </div>
            </Card>
          </div>

          {/* Carrier breakdown */}
          <div className="grid grid-cols-12 gap-4 mb-6">
            <Card className="col-span-7 p-5">
              <div className="text-[15px] font-semibold mb-3">{t('orphans.carrier_breakdown')}</div>
              {(ghostSummary?.by_carrier ?? []).length === 0 && <div className="text-[13px] text-ink-500">No ghost charges found.</div>}
              <div className="space-y-3">
                {(ghostSummary?.by_carrier ?? []).map(c => (
                  <div key={c.carrier}>
                    <div className="flex items-center justify-between text-[13px] mb-1">
                      <span className={`font-medium ${c.is_mysys_candidate ? 'text-ink-400' : 'text-ink-800'}`}>
                        {c.carrier}
                        {c.mysys_count > 0 && <span className="ml-2 px-1.5 py-0.5 rounded bg-blue-100 text-blue-600 text-[10px] font-semibold num">{c.is_mysys_candidate ? 'MYSYS' : `${FMT_INT.format(c.mysys_count)} MYSYS`}</span>}
                      </span>
                      <span className="num text-ink-500">{FMT_INT.format(c.count)} <span className="text-ink-400">({c.pct.toFixed(1)}%)</span></span>
                    </div>
                    {/* grey = MySYS rows (expected), amber = unexplained rows */}
                    <div className="h-2 rounded-full bg-ink-100 overflow-hidden flex">
                      <div className="h-full bg-ink-300"
                        style={{ width: `${(c.mysys_count / ghostMaxBarCount) * 100}%` }} />
                      <div className="h-full bg-amber-500"
                        style={{ width: `${((c.count - c.mysys_count) / ghostMaxBarCount) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
            <Card className="col-span-5 p-5">
              <div className="text-[15px] font-semibold mb-3">Legend</div>
              <div className="space-y-3 text-[13px]">
                <div className="flex items-center gap-3"><div className="w-3 h-3 rounded-full bg-ink-300 shrink-0" /><span className="text-ink-500">MySYS rows (expected)</span></div>
                <div className="flex items-center gap-3"><div className="w-3 h-3 rounded-full bg-amber-400 shrink-0" /><span>{t('orphans.age_warn')}</span></div>
                <div className="flex items-center gap-3"><div className="w-3 h-3 rounded-full bg-red-500 shrink-0" /><span>{t('orphans.age_danger')}</span></div>
              </div>
            </Card>
          </div>

          {/* Filters */}
          <Card className="p-4 mb-4">
            <div className="flex flex-wrap gap-3 items-center">
              {/* Carrier */}
              <select value={ghostCarrier} onChange={e => { setGhostCarrier(e.target.value); setGhostService(''); resetGhost() }}
                className="border border-ink-200 rounded-lg px-3 py-2 text-[13px] bg-white text-ink-700">
                <option value="">{t('orphans.filter_carrier')}</option>
                {(ghostSummary?.by_carrier ?? []).map(c => (
                  <option key={c.carrier} value={c.carrier}>{c.carrier} ({c.count})</option>
                ))}
              </select>

              {/* Service */}
              <select value={ghostService} onChange={e => { setGhostService(e.target.value); resetGhost() }}
                className="border border-ink-200 rounded-lg px-3 py-2 text-[13px] bg-white text-ink-700">
                <option value="">All services</option>
                {filteredGhostServices.map(s => (
                  <option key={`${s.carrier}:${s.service_type}`} value={s.service_type}>
                    {s.service_type} ({s.count})
                  </option>
                ))}
              </select>

              <div className="flex items-center gap-2">
                <span className="text-[12px] text-ink-500">{t('orphans.filter_date_from')}</span>
                <input type="date" value={ghostDateFrom} onChange={e => { setGhostDateFrom(e.target.value); resetGhost() }} className={dateInputClass} />
                <span className="text-[12px] text-ink-500">{t('orphans.filter_date_to')}</span>
                <input type="date" value={ghostDateTo} onChange={e => { setGhostDateTo(e.target.value); resetGhost() }} className={dateInputClass} />
                {(ghostDateFrom || ghostDateTo) && <button onClick={() => { setGhostDateFrom(''); setGhostDateTo(''); resetGhost() }} className="text-[12px] text-ink-400 hover:text-ink-700">✕</button>}
              </div>

              <AgePresets value={ghostMinAgeDays} onChange={v => { setGhostMinAgeDays(v); resetGhost() }} />

              <label className="flex items-center gap-2 text-[13px] text-ink-700 cursor-pointer">
                <input type="checkbox" checked={excludeMysys} onChange={e => { setExcludeMysys(e.target.checked); resetGhost() }} className="rounded border-ink-300" />
                {t('orphans.exclude_mysys')}
              </label>

              <button type="button" onClick={clearGhost} className="h-8 px-3 border border-ink-200 text-ink-500 rounded-md text-[12px] hover:bg-ink-50 transition-colors">Clear</button>

              {ghostSelCount > 0 && (
                <button onClick={handleIgnoreGhost} disabled={ignoreMutation.isPending}
                  className="ml-auto px-4 py-2 text-[13px] font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                  {ignoreMutation.isPending ? '…' : t('orphans.ignore_selected', { count: ghostSelCount })}
                </button>
              )}
            </div>
          </Card>

          {/* Table */}
          <Card className="overflow-hidden">
            {allGhostPageSelected && !selectAllGhostMatching && (
              <SelectAllBanner
                pageCount={currentGhostRows.length}
                total={ghostList?.total ?? 0}
                allMatchingSelected={false}
                onSelectMatching={() => setSelectAllGhostMatching(true)}
                onClearMatching={() => { setSelectAllGhostMatching(false); setSelectedGhostKeys(new Set()) }}
              />
            )}
            {selectAllGhostMatching && (
              <SelectAllBanner
                pageCount={currentGhostRows.length}
                total={ghostList?.total ?? 0}
                allMatchingSelected={true}
                onSelectMatching={() => {}}
                onClearMatching={() => { setSelectAllGhostMatching(false); setSelectedGhostKeys(new Set()) }}
              />
            )}

            {ghostLoading ? (
              <div className="p-8 text-center text-[13px] text-ink-500">Loading…</div>
            ) : currentGhostRows.length === 0 ? (
              <div className="p-8 text-center text-[13px] text-ink-500">No ghost charges found.</div>
            ) : (
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-ink-50 border-b border-ink-200">
                    <th className="px-3 py-3 w-8">
                      <input type="checkbox" checked={allGhostPageSelected || selectAllGhostMatching} onChange={toggleSelectAllGhost}
                        className="rounded border-ink-300 cursor-pointer" title={t('orphans.select_all')} />
                    </th>
                    <SortableHeader col="carrier"         label={t('orphans.carrier')}          sort={ghostSortState} onSort={onGhostSortClient} className="px-4 py-3" />
                    <SortableHeader col="tracking_number" label={t('orphans.tracking')}         sort={ghostSortState} onSort={onGhostSortClient} className="px-4 py-3" />
                    <SortableHeader col="invoice_date"    label={t('orphans.invoice_date')}     sort={ghostSortState} onSort={onGhostSortClient} className="px-4 py-3" />
                    <SortableHeader col="charge_amount"   label={t('orphans.charge')}           sort={ghostSortState} onSort={onGhostSortClient} className="px-4 py-3" numeric />
                    <SortableHeader col="days_on_invoice" label={t('orphans.days_on_invoice')}  sort={ghostSortState} onSort={onGhostSortClient} className="px-4 py-3" numeric />
                    <SortableHeader col="service_type"    label={t('orphans.service')}          sort={ghostSortState} onSort={onGhostSortClient} className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {sortedGhost.map((row, idx) => {
                    const k = ghostKey(row)
                    const sel = selectedGhostKeys.has(k) || selectAllGhostMatching
                    return (
                      <tr key={`${k}-${idx}`} className={`${ghostRowClass(row)} ${sel ? 'ring-1 ring-inset ring-royal-300' : ''}`}>
                        <td className="px-3 py-2.5">
                          <input type="checkbox" checked={sel} onChange={() => toggleGhostRow(row)} className="rounded border-ink-300 cursor-pointer" />
                        </td>
                        <td className="px-4 py-2.5 font-medium text-ink-700">
                          {row.carrier}
                          {row.is_mysys_candidate && <span className="ml-1.5 px-1 py-0.5 rounded bg-blue-100 text-blue-600 text-[10px] font-semibold">MYSYS?</span>}
                        </td>
                        <td className="px-4 py-2.5"><CopyCell value={row.tracking_number} /></td>
                        <td className="px-4 py-2.5 text-ink-500">{row.invoice_date ? row.invoice_date.slice(0, 10) : '—'}</td>
                        <td className="px-4 py-2.5 text-right num text-ink-700">
                          {row.charge_amount != null
                            ? `${row.charge_currency === 'GBP' ? '£' : '$'}${row.charge_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : '—'}
                        </td>
                        <td className={`px-4 py-2.5 text-right ${row.is_mysys_candidate ? 'num text-ink-400' : ageBadgeClass(row.days_on_invoice)}`}>
                          {row.days_on_invoice ?? '—'}
                        </td>
                        <td className="px-4 py-2.5 text-ink-500 max-w-[160px] truncate">{row.service_type ?? '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}

            {(ghostList?.total ?? 0) > 0 && (
              <Pagination total={ghostList?.total ?? 0} limit={ghostLimit} offset={ghostOffset}
                onPage={o => { setGhostOffset(o); setSelectAllGhostMatching(false) }}
                onLimitChange={l => { setGhostLimit(l); setGhostOffset(0); setSelectAllGhostMatching(false) }} />
            )}
          </Card>
        </>
      )}
    </div>
  )
}
